import React, { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Draggable } from "react-beautiful-dnd";
import { makeStyles } from "@mui/styles";
import ListItemButton from "@mui/material/ListItemButton";
import Avatar from "@mui/material/Avatar";
import LinkIcon from "@mui/icons-material/Link";

// My Files
import { UserContext, DatabaseContext } from "../../../../context";
import firebase from "../../../../firebase";
import { jsonFromArray } from "../../../../utility";
import { deleteFileRecordAndNotifications } from "../fileDeletion";
import ShareFileDialog from "../ShareFileDialog";
import DeleteFileDialog from "../DeleteFileDialog";
import DialogWithTextInput from "../../../DialogWithTextInput";
import {
    FinderFileContextMenu,
    FinderRowActionButton,
} from "./FinderActionMenus";
import { finderFileListItemStyles } from "./finderStyles";

const useStyles = makeStyles(finderFileListItemStyles);

export default function LinkListItem(props) {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    const { currentUserData } = useContext(UserContext);

    const [clickLocation, setClickLocation] = useState({
        mouseX: null,
        mouseY: null,
    });

    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const [fileNameDialogOpen, setFileNameDialogOpen] = useState(false);
    const [isSelected, setIsSelected] = useState(false); // Folder is selected when user right clicks on it
    const [shareFileDialogOpen, setShareFileDialogOpen] = useState(false);

    const { comments, events, users } =
        useContext(DatabaseContext);

    const [userIsAdmin, setUserIsAdmin] = useState(false);

    const [userCanShare, setUserCanShare] = useState(false);

    const [fileAllowsSharing, setFileAllowsSharing] = useState(false);

    useEffect(() => {
        if (currentUserData) {
            if (
                props.folder.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager" ||
                props.file.uploadedBy === currentUserData.key
            ) {
                setUserIsAdmin(true);
            } else {
                setUserIsAdmin(false);
            }

            if (currentUserData.permissions.shareFiles) {
                setUserCanShare(true);
            } else {
                setUserCanShare(false);
            }

            if (props.file.allowSharing) {
                setFileAllowsSharing(true);
            } else {
                setFileAllowsSharing(false);
            }
        }
    }, [props, currentUserData]);

    function handleClick() {
        history.push({
            pathname: `/link/${props.file.key}`,
            state: { returnTo: location.pathname },
        });
    }

    function handleRightClick(event) {
        event.preventDefault();

        // Checks that the current user is a manager for the folder or hub
        if (userIsAdmin) {
            setIsSelected(true);

            setClickLocation({
                mouseX: event.clientX - 2,
                mouseY: event.clientY - 4,
            });
        }
    }

    function handleActionButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const buttonBounds = event.currentTarget.getBoundingClientRect();
        setIsSelected(true);
        setClickLocation({
            mouseX: buttonBounds.right,
            mouseY: buttonBounds.bottom + 4,
        });
    }

    const handleMenuAction = (action) => {
        if (action === "renamefile") {
            setFileNameDialogOpen(true);
        } else if (action === "deletefile") {
            setConfirmDeleteDialogOpen(true);
        } else if (action === "duplicatefile") {
            createDuplicateFile();
        } else if (action === "sharefile") {
            setShareFileDialogOpen(true);
        }

        setClickLocation({
            mouseX: null,
            mouseY: null,
        });

        setIsSelected(false);
    };

    const handleFileRenameDialogClose = (fileName, confirmed) => {
        setFileNameDialogOpen(false);

        if (confirmed && userIsAdmin && fileName.trim() !== "") {
            const trimmedFileName = fileName.trim();
            let ref = firebase.database().ref("files").child(props.file.key);
            ref.update({ displayName: trimmedFileName });

            props.file.displayName = trimmedFileName;
        }
    };

    const handleConfirmDeleteDialogClose = (key) => () => {
        setConfirmDeleteDialogOpen(false);

        if (key === "OK") {
            if (userIsAdmin) {
                deleteFile();
            }
        }
    };

    function createDuplicateFile() {
        // creates a new entry for the file in the database, but it references the same file in storage
        var filesRef = firebase.database().ref("files");

        var original = props.file.key;

        if (props.file.original) {
            // Creating a duplicate of a duplicate, so the original should be the key of the original file
            original = props.file.original;
        }

        var uploadedBy = "";
        if (currentUserData) {
            uploadedBy = currentUserData.key;
        }

        filesRef.push({
            filename: props.file.filename,
            displayName: props.file.displayName + " Copy",
            folder: props.file.folder,
            type: props.file.type,
            allowDownloads: props.file.allowDownloads,
            allowComments: props.file.allowComments,
            uploadedBy: uploadedBy,
            managers: jsonFromArray(props.file.managers),
            groups: jsonFromArray(props.file.groups),
            ancestors: jsonFromArray(props.file.ancestors),
            url: props.file.url,
            original: original,
        });
    }

    async function deleteFile() {
        // Delete the comments
        comments.forEach((comment) => {
            if (comment.file === props.file.key) {
                let commentRef = firebase
                    .database()
                    .ref("comments")
                    .child(comment.key);
                commentRef.update({ deleted: true });
            }
        });


        // // Delete the file in any events

        events.forEach((event) => {
            let filesToKeepArray = [];
            let needsChange = false;
            event.files.forEach((aFile) => {
                if (aFile.key === props.file.key) {
                    needsChange = true;
                } else {
                    filesToKeepArray.push(aFile.key);
                }
            });

            if (needsChange) {
                let eventRef = firebase
                    .database()
                    .ref("events")
                    .child(event.key);

                // Change remaining files to JSON
                let filesObject = {};

                filesToKeepArray.forEach((aFile) => {
                    filesObject[aFile] = true;
                });

                eventRef.update({ files: filesObject });
            }
        });

        // Delete the file from any users (they are added here if the user is added manually to a file permissions when uploading)
        users.forEach((user) => {
            let filesToKeepArray = [];
            let needsChange = false;
            user.files.forEach((aFile) => {
                if (aFile === props.file.key) {
                    needsChange = true;
                } else {
                    filesToKeepArray.push(aFile);
                }
            });

            if (needsChange) {
                let userRef = firebase.database().ref("users").child(user.key);

                // Change remaining files to JSON
                let filesObject = {};

                filesToKeepArray.forEach((aFile) => {
                    filesObject[aFile] = true;
                });

                userRef.update({ files: filesObject });
            }
        });

        await deleteFileRecordAndNotifications(props.file.key);

        props.showStatusMessage("File deleted successfully", "success");
    }

    function handleCloseShareFileDialog() {
        setShareFileDialogOpen(false);
    }

    return (
        <Draggable
            draggableId={props.file.key}
            index={props.index}
            isDragDisabled={
                currentUserData.role === "Player" ||
                currentUserData.role === "FAW Player" ||
                currentUserData.role === "Club Player" ||
                currentUserData.role === "Coach Ed Student" ||
                currentUserData.role === "FAW Coach Ed Student" ||
                currentUserData.role === "Club Coach Ed Student" ||
                currentUserData.role === "FAW Parent" ||
                currentUserData.role === "Club Parent" ||
                currentUserData.role === "Referee"
            }
        >
            {(provided, snapshot) => (
                <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={classes.container}
                    ref={provided.innerRef}
                    onContextMenu={handleRightClick}
                >
                    <ListItemButton
                        onClick={handleClick}
                        className={`${classes.listItem} ${
                            snapshot.isDragging ? classes.dragging : ""
                        }`}
                        style={
                            isSelected
                                ? {
                                      boxShadow:
                                          "0 0 0 2px rgba(0, 149, 61, 0.48)",
                                  }
                                : {}
                        }
                    >
                        <Avatar
                            variant="rounded"
                            // src={image}
                            className={classes.thumbnail}
                        >
                            <LinkIcon />
                        </Avatar>
                        <p className={classes.label}>
                            {props.file.displayName}
                        </p>
                        {userIsAdmin && (
                            <FinderRowActionButton
                                label={`Actions for ${props.file.displayName}`}
                                onClick={handleActionButtonClick}
                            />
                        )}
                    </ListItemButton>
                    <FinderFileContextMenu
                        open={clickLocation.mouseY !== null}
                        position={{
                            top: clickLocation.mouseY,
                            left: clickLocation.mouseX,
                        }}
                        onClose={() => handleMenuAction()}
                        onAction={handleMenuAction}
                        canShare={userCanShare && fileAllowsSharing}
                    />
                    <DialogWithTextInput
                        open={fileNameDialogOpen}
                        onClose={handleFileRenameDialogClose}
                        initialValue={props.file.displayName}
                        title="Rename Link"
                        message="Enter a new name for this link."
                        confirmLabel="Rename"
                        modern
                    />
                    <DeleteFileDialog
                        open={confirmDeleteDialogOpen}
                        onClose={handleConfirmDeleteDialogClose("Cancel")}
                        onConfirm={handleConfirmDeleteDialogClose("OK")}
                        fileName={props.file.displayName}
                        itemType="link"
                    />
                    {shareFileDialogOpen && (
                        <ShareFileDialog
                            open={shareFileDialogOpen}
                            onClose={handleCloseShareFileDialog}
                            file={props.file}
                        />
                    )}
                </div>
            )}
        </Draggable>
    );
}
